import React from 'react';
import styled from 'styled-components';

import { translate } from 'translations/i18n';
import { Button } from '@companycam/slab-web';
import { Row, ErrorMessageRow } from '../CommonComponents';
import { type UseProfileFormReturn } from '../useProfileForm';
import { TextField } from '../FormFields/TextField';
import { StateField } from '../FormFields/StateField';
import { Steven } from 'helpers/steven';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_m);
`;

type CompanyAddressStepProps = {
  formMethods: UseProfileFormReturn;
};

export const CompanyAddressStepFooterActions = ({
  formMethods,
  nextStep,
}: {
  formMethods: UseProfileFormReturn;
  nextStep: () => void;
}) => {
  const onContinue = async () => {
    const isValid = await formMethods.trigger(['name', 'phoneNumber', 'email']);

    if (isValid) {
      // set city and state as service area
      const [city, state] = formMethods.getValues(['city', 'state']);
      if (city && state) {
        formMethods.setValue('serviceAreas', [`${city}, ${state}`]);
      }
      Steven.elementClicked({
        id: 'create_public_profile_address_continue',
        content: 'common.continue',
        type: 'button',
        action: 'create_public_profile_address_continue_clicked',
      });
      nextStep();
    }
  };

  return (
    <Button color="primary" onClick={onContinue}>
      {translate('common.continue')}
    </Button>
  );
};

export const CompanyAddressStep = ({
  formMethods,
}: CompanyAddressStepProps) => {
  const phoneOrEmailError =
    formMethods.formState.errors.phoneNumber?.message ||
    formMethods.formState.errors.email?.message;
  const nameError = formMethods.formState.errors.name?.message;

  return (
    <Container>
      <Row>
        <TextField
          data-testid="name"
          name="name"
          formMethods={formMethods}
          label={translate('common.companyName')}
          placeholder="My Company"
          status={nameError ? 'error' : undefined}
          message={nameError}
        />
      </Row>
      <Row>
        <TextField
          data-testid="addressLine1"
          name="addressLine1"
          formMethods={formMethods}
          label={translate('portfolio.profile.address1')}
          placeholder="123 Main St"
        />
        <TextField
          data-testid="addressLine2"
          name="addressLine2"
          formMethods={formMethods}
          label={translate('portfolio.profile.address2')}
          placeholder="Suite 100"
        />
      </Row>
      <Row>
        <TextField
          data-testid="city"
          name="city"
          formMethods={formMethods}
          label={translate('projects.forms.form.city')}
          placeholder={translate('projects.forms.form.city')}
        />
        <StateField formMethods={formMethods} />
        <TextField
          data-testid="postalCode"
          name="postalCode"
          formMethods={formMethods}
          label={translate('projects.forms.form.postalCode')}
          placeholder={translate('projects.forms.form.postalCode')}
        />
      </Row>
      <div>
        <Row>
          <TextField
            data-testid="phoneNumber"
            name="phoneNumber"
            formMethods={formMethods}
            label={translate('common.phoneNumber')}
            placeholder=""
            onChange={() => {
              formMethods.trigger('phoneNumber');
              formMethods.trigger('email');
            }}
          />
          <TextField
            data-testid="email"
            name="email"
            formMethods={formMethods}
            label={translate('common.emailAddress')}
            placeholder=""
            onChange={() => {
              formMethods.trigger('phoneNumber');
              formMethods.trigger('email');
            }}
          />
        </Row>
        <ErrorMessageRow errorMessage={phoneOrEmailError} />
      </div>
      <Row>
        <TextField
          data-testid="website"
          name="website"
          formMethods={formMethods}
          label={translate('portfolio.profile.website')}
          placeholder=""
        />
      </Row>
    </Container>
  );
};
